import { useState, useEffect } from "react";
import apiService from "../services/api";
import useWindowSize from "../hooks/useWindowSize";
import AssignmentSuccessModal from "./AssignmentSuccessModal";

/* ═══════════════════════ CONSTANTS ═══════════════════════ */
const W = "#ffffff";
const CARD_BG = "#042558";
const INPUT_BG = "rgba(255,255,255,0.08)";
const BORDER = "rgba(255,255,255,0.3)";
const ACCENT = "#4A90E2";
const GREEN = "#6ADD00";

const ASSIGNMENT_TYPES = ["PRIMARY", "SECONDARY"];
const SHIFT_TYPES = ["DAY", "EVENING", "NIGHT"];

/* ═══════════════════════ ASSIGN CAREGIVER MODAL ═══════════════════════ */
export default function AssignCaregiverModal({
  isOpen,
  onClose,
  residentId,
  residentName,
  onAssignmentComplete,
}) {
  const { isMobile } = useWindowSize();

  const [availableCaregivers, setAvailableCaregivers] = useState([]);
  const [filteredCaregivers, setFilteredCaregivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [assignmentType, setAssignmentType] = useState("PRIMARY");
  const [shiftType, setShiftType] = useState("DAY");
    const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [assignmentDetails, setAssignmentDetails] = useState(null);

  // Load available caregivers when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableCaregivers();
      // Reset state when modal opens
      setSelectedCaregiver(null);
      setAssignmentType("PRIMARY");
      setShiftType("DAY");
      setSearchTerm("");
      setError(null);
    }
  }, [isOpen]);

  // Filter caregivers based on search term
  useEffect(() => {
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      const filtered = availableCaregivers.filter(
        (cg) =>
          (cg.display_name || "").toLowerCase().includes(search) ||
          (cg.role || "").toLowerCase().includes(search) ||
          (cg.badge_id || "").toLowerCase().includes(search)
      );
      setFilteredCaregivers(filtered);
    } else {
      setFilteredCaregivers(availableCaregivers);
    }
  }, [searchTerm, availableCaregivers]);

  const loadAvailableCaregivers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAvailableCaregivers();
      setAvailableCaregivers(data.caregivers || []);
    } catch (err) {
      console.error("Failed to load available caregivers:", err);
      setError("Failed to load available caregivers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

    const handleAssign = async () => {
    if (!selectedCaregiver) {
      setError("Please select a caregiver");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

            await apiService.assignCaregiverToResident(residentId, {
        caregiver_id: selectedCaregiver.caregiver_id,
        assignment_type: assignmentType,
        shift_type: shiftType,
      });

      // Store assignment details for success modal
      setAssignmentDetails({
        caregiverName: selectedCaregiver.display_name || "Unknown Caregiver",
        residentName: residentName,
        assignmentType: assignmentType,
        shiftType: shiftType,
      });

      // Show success modal
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Failed to assign caregiver:", err);
      
      // Parse and format user-friendly error message
      let errorMessage = "Failed to assign caregiver. Please try again.";
      
      if (err.message) {
        // Remove HTTP status codes and technical prefixes
        let cleanMessage = err.message
          .replace(/^HTTP \d+:\s*/i, '') // Remove "HTTP 400: "
          .replace(/^Error:\s*/i, '')     // Remove "Error: "
          .replace(/^\{"detail":"(.+)"\}$/, '$1'); // Extract from JSON format
        
        // Check for specific error patterns and make them more user-friendly
        if (cleanMessage.includes('already assigned')) {
          // Extract caregiver name from the message if possible
          const caregiverName = selectedCaregiver?.display_name || 'This caregiver';
          const shiftMatch = cleanMessage.match(/(DAY|EVENING|NIGHT)/i);
          const shift = shiftMatch ? shiftMatch[1] : shiftType;
          
          errorMessage = `${caregiverName} is already assigned to another resident for the ${shift} shift. Please select a different caregiver or change the shift.`;
        } else {
          errorMessage = cleanMessage;
        }
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.7)",
          zIndex: 1050,
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1051,
          width: isMobile ? "90%" : "600px",
          maxHeight: "85vh",
          background: CARD_BG,
          border: `2px solid ${BORDER}`,
          borderRadius: 20,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "20px 20px 16px" : "30px 30px 16px",
            borderBottom: `1.5px solid ${BORDER}`,
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: isMobile ? 18 : 22,
              fontWeight: 700,
              color: W,
            }}
          >
            Assign Caregiver to {residentName}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: W,
              fontSize: 28,
              cursor: "pointer",
              padding: 0,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.7,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.7)}
          >
            ×
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: isMobile ? "20px" : "20px 30px",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* Error Message */}
          {error && (
          <div
            style={{
              background: "rgba(255,107,107,0.12)",
              border: "1.5px solid rgba(255,107,107,0.5)",
              borderRadius: 12,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FF6B6B"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span style={{ fontSize: 14, color: "#FF6B6B", flex: 1 }}>
              {error}
            </span>
            <button
              onClick={() => setError(null)}
              style={{
                background: "transparent",
                border: "none",
                color: "#FF6B6B",
                cursor: "pointer",
                fontSize: 18,
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Search Bar */}
        <div>
          <input
            type="text"
            placeholder="Search caregivers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              background: INPUT_BG,
              border: `1.5px solid ${BORDER}`,
              borderRadius: 10,
              padding: "12px 16px",
              fontSize: 14,
              color: W,
              fontFamily: "'Segoe UI', sans-serif",
              outline: "none",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = ACCENT)}
            onBlur={(e) => (e.target.style.borderColor = BORDER)}
          />
        </div>

        {/* Available Caregivers List */}
        <div>
          <h3
            style={{
              margin: "0 0 12px",
              fontSize: 16,
              fontWeight: 600,
              color: W,
              opacity: 0.85,
              letterSpacing: "0.3px",
            }}
          >
            Available Caregivers ({filteredCaregivers.length})
          </h3>

          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 20px",
                color: W,
                opacity: 0.6,
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke={W}
                strokeWidth="2"
                style={{ animation: "spin 1s linear infinite" }}
              >
                <path d="M21 12a9 9 0 11-6.219-8.56" />
              </svg>
              <span style={{ marginLeft: 12 }}>Loading caregivers...</span>
            </div>
          ) : filteredCaregivers.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 20px",
                color: W,
                opacity: 0.6,
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke={W}
                strokeWidth="1.5"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <p style={{ fontSize: 14, margin: "12px 0 0" }}>
                {searchTerm
                  ? "No caregivers found matching your search"
                  : "No available caregivers at this time"}
              </p>
            </div>
          ) : (
            <div
              className="custom-scrollbar"
              style={{
                maxHeight: "180px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                paddingRight: 8,
              }}
            >
              {filteredCaregivers.map((caregiver) => (
                <div
                  key={caregiver.caregiver_id}
                  onClick={() => setSelectedCaregiver(caregiver)}
                  style={{
                    background:
                      selectedCaregiver?.caregiver_id === caregiver.caregiver_id
                        ? "rgba(74,144,226,0.25)"
                        : "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${
                      selectedCaregiver?.caregiver_id === caregiver.caregiver_id
                        ? ACCENT
                        : BORDER
                    }`,
                    borderRadius: 12,
                    padding: "12px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      selectedCaregiver?.caregiver_id !== caregiver.caregiver_id
                    ) {
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.08)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      selectedCaregiver?.caregiver_id !== caregiver.caregiver_id
                    ) {
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.05)";
                    }
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      minHeight: "48px",
                    }}
                  >
                    {/* Radio Button */}
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        border: `2px solid ${
                          selectedCaregiver?.caregiver_id ===
                          caregiver.caregiver_id
                            ? ACCENT
                            : BORDER
                        }`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {selectedCaregiver?.caregiver_id ===
                        caregiver.caregiver_id && (
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: ACCENT,
                          }}
                        />
                      )}
                    </div>

                    {/* Caregiver Info */}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: W,
                          marginBottom: 6,
                          lineHeight: 1.2,
                        }}
                      >
                        {caregiver.display_name || "Unknown Caregiver"}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: W,
                          opacity: 0.7,
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap",
                          lineHeight: 1.4,
                        }}
                      >
                        <span>Shift: {caregiver.primary_shift || "N/A"}</span>
                        <span>
                          Load: {caregiver.current_resident_count || 0}/
                          {caregiver.max_resident_load || 10}
                        </span>
                        <span
                          style={{
                            color: GREEN,
                            fontWeight: 600,
                          }}
                        >
                          Available
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assignment Type Selection */}
        <div>
          <h3
            style={{
              margin: "0 0 12px",
              fontSize: 14,
              fontWeight: 600,
              color: W,
              opacity: 0.85,
              letterSpacing: "0.5px",
            }}
          >
            Assignment Type
          </h3>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {ASSIGNMENT_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setAssignmentType(type)}
                style={{
                  flex: 1,
                  minWidth: "120px",
                  background:
                    assignmentType === type
                      ? "rgba(74,144,226,0.3)"
                      : "rgba(255,255,255,0.05)",
                  border: `1.5px solid ${
                    assignmentType === type ? ACCENT : BORDER
                  }`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontSize: 14,
                  fontWeight: assignmentType === type ? 700 : 400,
                  color: W,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  textAlign: "center",
                }}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Shift Type Selection */}
        <div>
          <h3
            style={{
              margin: "0 0 12px",
              fontSize: 14,
              fontWeight: 600,
              color: W,
              opacity: 0.85,
              letterSpacing: "0.5px",
            }}
          >
            Shift Type
          </h3>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {SHIFT_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setShiftType(type)}
                style={{
                  flex: 1,
                  minWidth: "100px",
                  background:
                    shiftType === type
                      ? "rgba(74,144,226,0.3)"
                      : "rgba(255,255,255,0.05)",
                  border: `1.5px solid ${shiftType === type ? ACCENT : BORDER}`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontSize: 14,
                  fontWeight: shiftType === type ? 700 : 400,
                  color: W,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  textAlign: "center",
                }}
              >
                {type}
              </button>
            ))}
          </div>
          </div>
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            padding: isMobile ? "16px 20px 20px" : "20px 30px 30px",
            borderTop: `1.5px solid ${BORDER}`,
            flexShrink: 0,
            background: CARD_BG,
          }}
        >
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: `1.5px solid ${BORDER}`,
              borderRadius: 10,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 600,
              color: W,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.5 : 1,
              transition: "all 0.2s",
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleAssign}
            disabled={submitting || !selectedCaregiver}
            style={{
              background: submitting || !selectedCaregiver
                ? "rgba(106,221,0,0.3)"
                : "linear-gradient(135deg, #6ADD00, #4CAF50)",
              border: "none",
              borderRadius: 10,
              padding: "12px 32px",
              fontSize: 14,
              fontWeight: 700,
              color: submitting || !selectedCaregiver ? "rgba(255,255,255,0.6)" : W,
              cursor: submitting || !selectedCaregiver ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: submitting || !selectedCaregiver
                ? "none"
                : "0 4px 16px rgba(106,221,0,0.3)",
            }}
          >
            {submitting ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={W}
                  strokeWidth="2"
                  style={{ animation: "spin 1s linear infinite" }}
                >
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                Assigning...
              </>
            ) : (
              "Assign Caregiver"
            )}
          </button>
        </div>
      </div>



      {/* Success Modal */}
      {showSuccessModal && assignmentDetails && (
        <AssignmentSuccessModal
          isOpen={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            setAssignmentDetails(null);
            
            // Notify parent component
            if (onAssignmentComplete) {
              onAssignmentComplete();
            }
            
            // Close the assignment modal
            onClose();
          }}
          caregiverName={assignmentDetails.caregiverName}
          residentName={assignmentDetails.residentName}
          assignmentType={assignmentDetails.assignmentType}
          shiftType={assignmentDetails.shiftType}
        />
      )}
    </>
  );
}
